import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { connect } from 'react-redux';
import get from 'lodash.get';
import * as tracking from 'dibs-tracking';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { PdpRespPreviewMessageBanner } from '../PdpRespPreviewMessageBanner/PdpRespPreviewMessageBanner';
import { PdpRespPrintHeader } from '../PdpRespPrintHeader/PdpRespPrintHeader';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespBreadcrumbs } from '../PdpRespBreadcrumbs/PdpRespBreadcrumbs';
import { PdpRespHeroCarousel } from '../PdpRespHeroCarousel/PdpRespHeroCarousel';
import { PdpRespHeroCarouselPrint } from '../PdpRespHeroCarousel/PdpRespHeroCarouselPrint';
import { PdpRespTitle } from '../PdpRespTitle/PdpRespTitle';
import { PdpSharedSidebarFavorites } from '../PdpSharedSidebarFavorites/PdpSharedSidebarFavorites';
import { PriceDisplay } from 'dibs-buyer-price';
import { PdpRespSidebarButtons } from '../PdpRespSidebarButtons/PdpRespSidebarButtons';
import { PdpRespSidebarBlurbs } from '../PdpRespSidebarBlurbs/PdpRespSidebarBlurbs';
import { PdpRespAboutModule } from '../PdpRespAboutModule/PdpRespAboutModule';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpRespBuyerAssuranceBox } from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import { PdpRespStorytellingModule } from '../PdpRespStorytellingModule/PdpRespStorytellingModule';
import { PdpRespSidebarSellerInfo } from '../PdpRespSidebarSellerInfo/PdpRespSidebarSellerInfo';
import { PdpRespSidebarContact } from '../PdpRespSidebarContact/PdpRespSidebarContact';
import { PdpRespSimilarItemsFooter } from '../PdpRespSimilarItems/PdpRespSimilarItemsFooter';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpSharedQuantitySelect } from '../PdpSharedButtons/PdpSharedQuantitySelect';
import { PdpRespRingSize } from '../PdpRespRingSize/PdpRespRingSize';
import { PdpSharedReminderLoader } from '../PdpSharedReminder/PdpSharedReminderLoader';
import { PdpSharedRecentHistoryTracker } from '../PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTracker';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';
import { SharedRecentlyViewedItems } from '../../shared/SharedRecentlyViewedItems/SharedRecentlyViewedItems';
import { PdpSharedRestrictedMaterialsLoader } from '../PdpSharedRestrictedMaterials/PdpSharedRestrictedMaterialsLoader';
import { PdpRespQrCode } from '../PdpRespQrCode/PdpRespQrCode';
import { PdpRespSwatchSelector } from '../PdpRespSwatchSelector/PdpRespSwatchSelector';
import { PdpSharedLeadTime } from '../PdpSharedLeadTime/PdpSharedLeadTime';
import { PdpRespExitModalLazy } from '../PdpRespExitModal/PdpRespExitModalLazy';
import { PdpSharedShippingBadgeLazy } from '../PdpSharedShippingBadge/PdpSharedShippingBadgeLazy';
import { PdpSharedFrameIncludedLazy } from '../PdpSharedFrameIncluded/PdpSharedFrameIncludedLazy';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock';

// callouts
import { NOTIFICATION_KEYS as SHARED_NOTIFICATION_KEYS } from '../PdpSharedNotifications/pdpSharedNotificationConstants';
import { SharedNotificationsWrapper } from '../../shared/SharedNotificationsWrapper';
import { PdpSharedNotificationMultiplePdpVisitsModalLoader } from '../PdpSharedNotifications/PdpSharedNotificationMultiplePdpVisitsModal/PdpSharedNotificationMultiplePdpVisitsModalLoader';
import { getSelectedSku } from '../helpers/getSelectedSku';

// helpers
import classnames from 'classnames';
import { useContactDealerAutoModal } from 'dibs-buyer-layout/exports/contactDealerLauncher';
import { trackPdpViewMoreCtaClick } from 'dibs-recent-history/src/recentHistoryTracking';

// context
import { PdpSharedStorytellingTrackingContextProvider } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';

// styles
import styles from './PdpRespContainer.scss';

/* eslint-disable react/prop-types */
// disable for function as child of PdpRespNotificationsWrapper

export function PdpRespContainerComponent(props) {
    useEffect(() => {
        const makeOfferTrackingData = getAbTestTrackingData(
            document.cookie,
            'REQUIRED_MESSAGE_WITH_OFFER'
        );
        if (makeOfferTrackingData?.testGroup) {
            tracking.trackAbTestEvent(makeOfferTrackingData, 'PDP view');
        }
    }, []);

    const {
        printWithPrice,
        currency,
        viewer,
        user,
        isPdpRespPreviewPage,
        item,
        displayInfo,
        similarLandingItems,
        vipUserRelationship,
        shipmentQuote,
        shipment,
        relayVariables,
        relay,
        selectedSkuId,
        onSkuChange,
    } = props;
    const {
        isMultiSku,
        itemId,
        userId,
        isClient,
        isTrade,
        fetchUser,
        showSimilarItems,
        isVip,
    } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isNewAndCustom = get(item, 'itemType') === 'NEW_MADE_TO_ORDER';
    const selectedSkuLeadTime = get(selectedSku, 'skuLeadTime');

    useContactDealerAutoModal({
        itemId,
        userId,
        environment: relay.environment,
        currency,
    });

    const scrollToRef = useRef(null);
    const [hasFreeShipping, setHasFreeShipping] = useState(null);
    const [hasFrameIncluded, setHasFrameIncluded] = useState(null);

    const shippingBadge = (
        <PdpSharedShippingBadgeLazy
            item={item}
            viewer={viewer}
            setHasFreeShipping={setHasFreeShipping}
        />
    );
    const frameIncluded = (
        <PdpSharedFrameIncludedLazy item={item} setHasFrameIncluded={setHasFrameIncluded} />
    );

    return (
        <div>
            {/* Preview Page Message Banner */}
            {isPdpRespPreviewPage && <PdpRespPreviewMessageBanner />}

            {/* Print Header */}
            {isClient && <PdpRespPrintHeader itemId={itemId} className={styles.printShow} />}

            {/* Similar Items */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsLanding
                    relayVariables={{
                        isClient,
                        showSimilarItems,
                    }}
                    scrollToRef={scrollToRef}
                    item={item}
                    similarItems={similarLandingItems}
                    viewer={viewer}
                    user={user}
                    vipUserRelationship={vipUserRelationship}
                />
            </div>

            {/* Notification reminder loader */}
            <PdpSharedReminderLoader
                relayVariables={{
                    userId,
                    fetchUser,
                    itemId,
                    isClient,
                }}
                environment={relay.environment}
                load={!!user}
            />

            {/* PdpBreadcrumbs */}
            <div className={styles.printHide}>
                <PdpRespBreadcrumbs item={item} />
            </div>

            <div className="rowFlex">
                <div className={classnames(styles.topContainer, 'colXs12 colLg7 colXl8')}>
                    {/* Image Carousel */}
                    <div className={styles.printHide}>
                        <PdpRespHeroCarousel
                            itemTitle={item.title || ''}
                            itemImage={item.firstPhotoWebPath || ''}
                            item={item}
                            selectedSkuId={selectedSkuId}
                            user={user}
                            environment={relay.environment}
                            relayVariables={{
                                userId,
                                fetchUser,
                                itemId,
                                isClient,
                                isTrade,
                            }}
                            viewer={viewer || null}
                            disableZoomModal={isPdpRespPreviewPage}
                            disableRequestMoreImagesModal={isPdpRespPreviewPage}
                        />
                    </div>
                    {/* Stripped Image Carousel (print view) */}
                    {isClient && (
                        <div className={styles.printShow}>
                            <PdpRespHeroCarouselPrint item={item} selectedSkuId={selectedSkuId} />
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className={`${styles.topContainer} colXs12 colLg5 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        <div className="rowFlex">
                            {/* Title */}
                            <div className="colXs12 colLg10">
                                <PdpRespTitle item={item} />
                            </div>
                            {/* Favorite / Folder */}
                            {viewer.isFavoritesEnabled && (
                                <div className={`${styles.printHide} colXs2`}>
                                    <PdpSharedSidebarFavorites
                                        environment={relay.environment}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        itemTitle={item.title || ''}
                                        itemImage={item.firstPhotoWebPath || ''}
                                        itemId={itemId}
                                        isClient={isClient}
                                        userId={userId}
                                        wrapperClass={styles.favoritesWrapper}
                                    />
                                </div>
                            )}
                        </div>

                        {isClient && (
                            <div className={styles.shippingBadgeContainer}>
                                {shippingBadge}
                                {hasFreeShipping && hasFrameIncluded && (
                                    <span className={styles.divider}>|</span>
                                )}
                                {frameIncluded}
                            </div>
                        )}
                        <div
                            className={classnames({
                                [styles.printHide]: !printWithPrice,
                            })}
                        >
                            <div className={styles.priceWithAccessoryInlineContainer}>
                                <PriceDisplay
                                    currency={currency}
                                    item={item}
                                    useSoldIcon
                                    sku={isMultiSku ? selectedSku : null}
                                />
                            </div>
                            <div className={styles.printHide}>
                                <PdpSharedPromoCode
                                    displayInfo={displayInfo}
                                    itemId={itemId}
                                    user={user}
                                    userId={userId}
                                    excludeShippingPromos
                                />
                            </div>
                        </div>
                        {isMultiSku && (
                            <PdpRespSwatchSelector
                                item={item}
                                user={user}
                                selectedSkuId={selectedSkuId}
                                onClick={skuId => {
                                    onSkuChange(skuId);
                                }}
                                showSwatchDescriptionOnHover
                            />
                        )}
                        {isNewAndCustom && selectedSkuLeadTime && (
                            <div className={styles.leadTimeContainer}>
                                <PdpSharedLeadTime
                                    leadTime={selectedSkuLeadTime}
                                    valueClassName={styles.value}
                                />
                            </div>
                        )}
                        {/* Item Ctas */}
                        {isClient && (
                            <div className={`${styles.printHide} ${styles.itemCtasWrapper}`}>
                                <PdpSharedQuantitySelect
                                    item={item}
                                    viewer={viewer}
                                    selectedSkuId={selectedSkuId}
                                >
                                    {function quantitySelection(quantitySelected) {
                                        return (
                                            <PdpRespSidebarButtons
                                                viewer={viewer}
                                                currency={currency}
                                                relayVariables={{
                                                    userId,
                                                    isMultiSku,
                                                    itemId,
                                                }}
                                                isClient={isClient}
                                                user={user}
                                                item={item}
                                                displayInfo={displayInfo}
                                                quantitySelected={quantitySelected}
                                                skuId={selectedSkuId}
                                            />
                                        );
                                    }}
                                </PdpSharedQuantitySelect>
                            </div>
                        )}

                        {viewer?.isRingSizesOn ? (
                            <PdpRespRingSize isClient={isClient} item={item} />
                        ) : null}

                        {/* Restricted Materials */}
                        {isClient && <PdpSharedRestrictedMaterialsLoader viewer={viewer} />}

                        {/* Blurbs */}
                        <PdpRespSidebarBlurbs
                            currency={currency}
                            relayVariables={{
                                userId,
                                itemId,
                                isTrade,
                                isVip,
                                isClient,
                            }}
                            viewer={viewer}
                            item={item}
                            user={user}
                            displayInfo={displayInfo}
                            shipmentQuote={shipmentQuote}
                            shipment={shipment}
                            sku={isMultiSku ? selectedSku : null}
                            showLeadTimeBlurb={!isNewAndCustom && !isMultiSku}
                        />
                        {isClient && (
                            <div className={styles.printHide}>
                                <div className={styles.sidebarSection}>
                                    {/* Questions about this piece? / Contact Dealer */}
                                    <PdpRespSidebarContact
                                        displayInfo={displayInfo}
                                        item={item}
                                        user={user}
                                        viewer={viewer}
                                        itemId={itemId}
                                        userId={userId}
                                    />

                                    {/* Free Shipping Seller Stats Variant*/}
                                    <div className={styles.sellerInfoContainer}>
                                        <PdpRespSidebarSellerInfo
                                            seller={item.seller}
                                            viewer={viewer}
                                            relay={relay}
                                            user={user}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        <PdpRespQrCode className={styles.printShow} itemId={itemId} />
                    </div>
                </div>
            </div>
            <div className={styles.details}>
                {/* About */}
                <PdpRespAboutModule
                    item={item}
                    user={user}
                    currency={currency}
                    selectedSkuId={selectedSkuId}
                    viewer={viewer}
                />
                {/* Pdp Dealer Chat */}
                {isClient && <PdpRespDealerChatLoader viewer={viewer} item={item} />}
                {/* Shipping and Returns */}
                <PdpRespShippingReturns
                    currency={currency}
                    relayVariables={{
                        userId,
                        itemId,
                        isClient,
                    }}
                    viewer={viewer}
                    user={user}
                    item={item}
                    shipmentQuote={shipmentQuote}
                    shipment={shipment}
                />
                <PdpSharedStorytellingTrackingContextProvider>
                    {/* Storytelling Module */}
                    <div className={styles.printHide}>
                        <PdpRespStorytellingModule item={item} />
                    </div>
                </PdpSharedStorytellingTrackingContextProvider>
            </div>

            {/* Contact Dealer Details */}
            <div className={styles.printHide}>
                <PdpRespDealerDetails
                    user={user}
                    item={item}
                    relay={relay}
                    viewer={viewer}
                    currency={currency}
                    isClient={isClient}
                />
            </div>

            {/* You May Also Like */}
            <div className={styles.printHide}>
                <PdpRespSimilarItemsFooter
                    currency={currency}
                    viewer={viewer}
                    user={user}
                    item={item}
                    scrollToRef={scrollToRef}
                    relayVariables={relayVariables}
                />
            </div>
            {/* Recently Viewed Items */}
            <div className={`${styles.printHide} ${styles.recentlyViewed}`}>
                <SharedRecentlyViewedItems
                    itemId={item.serviceId}
                    isClient={isClient}
                    user={user}
                    onViewMoreCtaClick={trackPdpViewMoreCtaClick}
                />
            </div>
            {/* Photos - todo (INTL-70: PDP follow on) */}
            {isClient && !viewer.disablePdpExitModal && !userId && (
                <PdpRespExitModalLazy item={item} />
            )}
            <SharedNotificationsWrapper type={SHARED_NOTIFICATION_KEYS.MULTI_PDP_VISITS}>
                {({ isEnabled }) => (
                    <PdpSharedNotificationMultiplePdpVisitsModalLoader
                        isEnabled={viewer.isFavoritesEnabled && isEnabled && !isPdpRespPreviewPage}
                        itemId={itemId}
                        environment={relay.environment}
                    />
                )}
            </SharedNotificationsWrapper>

            {/* Recent History */}
            {!isPdpRespPreviewPage && <PdpSharedRecentHistoryTracker item={item} />}

            {/* Buyer Assurance Box */}
            <div className={styles.printHide}>
                <PdpRespBuyerAssuranceBox user={user} />
            </div>

            {/* More Ways To Browse */}
            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    viewer={viewer}
                    item={item}
                    links={item.internalBuyLinks}
                    itemSearch={null}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                />
            </div>
        </div>
    );
}

PdpRespContainerComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        isMultiSku: PropTypes.bool.isRequired,
        isTrade: PropTypes.bool,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool.isRequired,
        skuId: PropTypes.string,
    }).isRequired,
    onSkuChange: PropTypes.func.isRequired,
    relay: PropTypes.object.isRequired,

    // relay response data
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    displayInfo: PropTypes.object,
    similarLandingItems: PropTypes.object,
    vipUserRelationship: PropTypes.object,
    shipmentQuote: PropTypes.object,

    // from redux
    printWithPrice: PropTypes.bool.isRequired,
    openTradeAskSellerTooltip: PropTypes.func,
};

function mapStateToProps({ pdpPrint, pdpPreviewPage }) {
    return {
        printWithPrice: pdpPrint.printWithPrice,
        isPdpRespPreviewPage: pdpPreviewPage.isPdpRespPreviewPage,
    };
}

export const PdpRespContainer = createFragmentContainer(
    connect(mapStateToProps)(PdpRespContainerComponent),
    {
        viewer: graphql`
            fragment PdpRespContainer_viewer on Viewer {
                ...SharedRespLinksBlock_viewer
                ...PdpRespSidebarBlurbs_viewer
                ...PdpRespSidebarButtons_viewer
                ...PdpRespShippingReturns_viewer
                ...PdpRespSimilarItemsFooter_viewer
                ...PdpRespHeroCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespDealerDetails_viewer
                ...PdpRespSidebarContact_viewer
                ...PdpRespSidebarSellerInfo_viewer
                ...PdpSharedShippingBadge_viewer
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                ...PdpRespAboutModule_viewer
                ...PdpSharedRestrictedMaterialsLoader_viewer
                ... @include(if: $fetchPdp) {
                    isFavoritesEnabled: isEnabledByLocale(featureName: "FAVORITES")
                    disablePdpExitModal: isEnabled(key: "disablePdpExitModal")
                    isAboutTheMakerEnabled: isEnabledByLocale(featureName: "ABOUT_THE_MAKER")
                    isRingSizesOn: featureFlag(feature: "ringSizes")
                }
            }
        `,
        user: graphql`
            fragment PdpRespContainer_user on User {
                ...PdpRespSimilarItemsFooter_user
                ...PdpRespSidebarButtons_user
                ...PdpRespSidebarBlurbs_user
                ...PdpRespShippingReturns_user
                ...PdpRespBuyerAssuranceBox_user
                ...PdpRespSidebarContact_user
                ...PdpRespHeroCarousel_user
                ...PdpRespDealerDetails_user
                ...PdpRespSidebarSellerInfo_user
                ...PdpSharedPromoCode_user
                ...PdpRespSwatchSelector_user @include(if: $isMultiSku)
                ...SharedRecentlyViewedItems_user
                ...PdpRespAboutModule_user
            }
        `,
        item: graphql`
            fragment PdpRespContainer_item on Item {
                serviceId
                firstPhotoWebPath(size: small)
                title
                defaultSkuId
                itemType
                ...PdpRespStorytellingModule_item
                ...PdpRespExitModal_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespBreadcrumbs_item
                ...PdpRespHeroCarouselPrint_item
                ...PdpRespTitle_item
                ...PdpRespSidebarButtons_item
                ...PdpRespSidebarBlurbs_item
                ...PdpRespShippingReturns_item
                ...PdpRespSidebarContact_item
                ...PdpRespSimilarItemsFooter_item
                ...PdpSharedQuantitySelect_item
                ...PdpSharedRecentHistoryTracker_item
                ...SharedRespLinksBlock_item
                ...PriceDisplay_item
                @arguments(hasUserId: $fetchUser, userId: $userId, isTrade: $isTrade)
                ...PdpRespSwatchSelector_item @include(if: $isMultiSku)
                ...PdpSharedShippingBadge_item
                ...PdpSharedFrameIncluded_item
                ...PdpRespDealerDetails_item
                ...PdpRespAboutModule_item
                ...PdpRespRingSize_item
                ... @include(if: $fetchPdp) {
                    seller {
                        ...PdpRespSidebarSellerInfo_seller
                    }
                    ...PdpRespHeroCarousel_item
                    internalBuyLinks {
                        ...SharedRespLinksBlock_links
                    }
                }
                skus {
                    serviceId
                    skuLeadTime: leadTime(pageType: PDP)
                    ...PriceDisplay_sku
                    @include(if: $isMultiSku)
                    @arguments(
                        hasUserId: $fetchUser
                        hasSkuId: true
                        userId: $userId
                        isTrade: $isTrade
                    )
                    ...PdpRespSidebarBlurbs_sku @include(if: $isMultiSku)
                }
            }
        `,
        displayInfo: graphql`
            fragment PdpRespContainer_displayInfo on ItemDisplayInfo {
                ...PdpRespSidebarButtons_displayInfo
                ...PdpRespSidebarContact_displayInfo
                ...PdpSharedPromoCode_displayInfo
                ...PdpRespSidebarBlurbs_displayInfo
                ...PdpSharedPromoCode_displayInfo
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
        shipmentQuote: graphql`
            fragment PdpRespContainer_shipmentQuote on ShipmentQuote {
                ...PdpRespSidebarBlurbs_shipmentQuote
                ...PdpRespShippingReturns_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpRespContainer_shipment on Shipment {
                ...PdpRespSidebarBlurbs_shipment
                ...PdpRespShippingReturns_shipment
            }
        `,
    }
);
